import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { Button, TextField } from '../ui'
import { Select } from '../ui/Select'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import { TableEmptyRow, TableLoadingRow } from '../governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import styles from '../../styles/components/assets/ScanJobsResultsSection.module.scss'

export type ScanJobRow = {
  id: string
  organizationId?: string
  userId?: string
  scannerType?: string
  status?: string
  startedAt?: string
  finishedAt?: string
  criticalCount?: number
  highCount?: number
}

type ScanFilters = {
  organization_id?: string
  scannerType?: string
  status?: string
  from?: string
  to?: string
}

type Props = {
  defaultOrganizationId?: string
}

export default function ScanJobsResultsSection({ defaultOrganizationId }: Props) {
  const { t } = useI18n()
  const { token, user } = useAuth()
  const isAdmin = (user as { role?: string } | null)?.role === 'admin'

  const [scanJobs, setScanJobs] = useState<ScanJobRow[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<ScanFilters>(() => ({
    organization_id: defaultOrganizationId?.trim() || undefined,
  }))

  useEffect(() => {
    if (!defaultOrganizationId) return
    setFilters((prev) => ({
      ...prev,
      organization_id: prev.organization_id || defaultOrganizationId,
    }))
  }, [defaultOrganizationId])

  const scannerOptions = useMemo(
    () => [
      { value: '', label: t('assets.scanJobs.allScanners', 'All scanners') },
      { value: 'openvas', label: 'OpenVAS' },
      { value: 'cve', label: 'CVE' },
    ],
    [t],
  )

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('assets.scanJobs.allStatuses', 'All statuses') },
      { value: 'Running', label: 'Running' },
      { value: 'Done', label: 'Done' },
      { value: 'Failed', label: 'Failed' },
    ],
    [t],
  )

  const fetchScanJobs = useCallback(async () => {
    if (!token) return
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.organization_id) params.set('organization_id', filters.organization_id)
      if (filters.scannerType) params.set('scannerType', filters.scannerType)
      if (filters.status) params.set('status', filters.status)
      if (filters.from) params.set('from', filters.from)
      if (filters.to) params.set('to', filters.to)
      const res = await fetch(`/api/admin/scan-jobs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setScanJobs(Array.isArray(data.scans) ? data.scans : [])
      } else {
        setScanJobs([])
      }
    } catch {
      setScanJobs([])
    } finally {
      setLoading(false)
    }
  }, [token, filters])

  useEffect(() => {
    if (!isAdmin) return
    void fetchScanJobs()
  }, [isAdmin, fetchScanJobs])

  if (!isAdmin) return null

  return (
    <div className={styles.root}>
      <h2 className={listStyles.panelExternalTitle}>
        {t('assets.scanJobs.title', 'Scan Jobs & Results')}
      </h2>
      <GovernanceListingSection
        toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                <TextField
                  label={t('assets.scanJobs.organizationId', 'Organization ID')}
                  value={filters.organization_id ?? ''}
                  onChange={(v) => setFilters((prev) => ({ ...prev, organization_id: v }))}
                  placeholder={t('assets.scanJobs.organizationFilter', 'Filter by organization')}
                  minWidth="12rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('assets.scanJobs.scanner', 'Scanner')}
                  minWidth="10rem"
                  options={scannerOptions}
                  value={filters.scannerType ?? ''}
                  onChange={(v) => setFilters((prev) => ({ ...prev, scannerType: v ?? '' }))}
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('assets.scanJobs.status', 'Status')}
                  minWidth="10rem"
                  options={statusOptions}
                  value={filters.status ?? ''}
                  onChange={(v) => setFilters((prev) => ({ ...prev, status: v ?? '' }))}
                />
              </div>
              <div className={listStyles.toolbarField}>
                <span className={listStyles.fieldLabel}>{t('assets.scanJobs.from', 'From')}</span>
                <input
                  type="date"
                  className={listStyles.dateInput}
                  value={filters.from || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
                />
              </div>
              <div className={listStyles.toolbarField}>
                <span className={listStyles.fieldLabel}>{t('assets.scanJobs.to', 'To')}</span>
                <input
                  type="date"
                  className={listStyles.dateInput}
                  value={filters.to || ''}
                  onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
                />
              </div>
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button type="button" size="small" color="outline" onClick={() => void fetchScanJobs()}>
                  {t('common.refresh', 'Refresh')}
                </Button>
              </div>
              <div className={listStyles.toolbarField}>
                <Button type="button" size="small" color="primary" onClick={() => void fetchScanJobs()}>
                  {t('common.apply', 'Apply')}
                </Button>
              </div>
            </div>
          </div>
        }
      >
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                <th className={listStyles.th}>{t('assets.scanJobs.colOrganization', 'Organization')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colUser', 'User')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colScanner', 'Scanner')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colStatus', 'Status')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colStarted', 'Started')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colFinished', 'Finished')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colCritical', 'Critical')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colHigh', 'High')}</th>
                <th className={listStyles.th}>{t('assets.scanJobs.colReport', 'Report')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableLoadingRow colSpan={9}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              ) : scanJobs.length === 0 ? (
                <TableEmptyRow colSpan={9}>
                  {t('assets.scanJobs.empty', 'No scan jobs match your filters.')}
                </TableEmptyRow>
              ) : (
                scanJobs.map((j) => (
                  <tr key={j.id} className={listStyles.row}>
                    <td className={listStyles.td}>{j.organizationId}</td>
                    <td className={listStyles.td}>{j.userId}</td>
                    <td className={listStyles.td}>{j.scannerType}</td>
                    <td className={listStyles.td}>{j.status}</td>
                    <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                      {j.startedAt ? new Date(j.startedAt).toLocaleString() : ''}
                    </td>
                    <td className={`${listStyles.td} ${listStyles.cellMuted}`}>
                      {j.finishedAt ? new Date(j.finishedAt).toLocaleString() : ''}
                    </td>
                    <td className={listStyles.td}>{j.criticalCount ?? 0}</td>
                    <td className={listStyles.td}>{j.highCount ?? 0}</td>
                    <td className={listStyles.td}>
                      <a
                        className={styles.reportLink}
                        href={`/api/scan-provider/scans/${encodeURIComponent(j.id)}/report`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('assets.scanJobs.download', 'Download')}
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GovernanceListingSection>
    </div>
  )
}
